import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import api from '../../services/api';
import {
  HEADING_LEVEL_OPTIONS,
  clampHeadingLevel,
  defaultCarouselCity,
  defaultCarouselEntry,
  defaultCarouselSlide,
  hasCarouselEntryImage,
  normalizeCarouselEntryImage,
  portsFromEntryPage,
} from './countrySectionCatalog';
import { BORDER_CITIES_BY_STATE, BORDER_STATES } from '../../constants/constants';

function moveItem(list, index, dir) {
  const target = index + dir;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const tmp = next[index];
  next[index] = next[target];
  next[target] = tmp;
  return next;
}

function openCloudinary(onResult) {
  if (!window.cloudinary?.openUploadWidget) return;
  window.cloudinary.openUploadWidget(
    { cloudName: 'dtxar5b36', uploadPreset: 'bordergarita_uploads' },
    (error, result) => {
      if (!error && result.event === 'success') {
        onResult({
          url: result.info.secure_url,
          width: result.info.width,
          height: result.info.height,
        });
      }
    }
  );
}

function scrollToId(id) {
  setTimeout(() => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, 80);
}

function pageMatchesLang(page, langKey) {
  const langUpper = String(langKey || '').toUpperCase();
  if (!langUpper) return true;
  const pageLang = String(page?.lang || '').toUpperCase();
  if (pageLang && pageLang === langUpper) return true;

  const slug = String(page?.full_slug || page?.slug || '').toLowerCase();
  const prefix = `/${String(langKey || '').toLowerCase()}/`;
  if (slug.startsWith(prefix) || slug === `/${String(langKey || '').toLowerCase()}`) {
    return true;
  }
  return false;
}

function toPageOption(page) {
  const id = page?._id != null ? String(page._id) : '';
  const slug = page?.full_slug || page?.slug || id;
  const name =
    page?.crossing_names_section?.name || page?.title || '';
  const label = name ? `${slug} · ${name}` : slug;
  return {
    label,
    value: id,
    page,
  };
}

function normalizeMatchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

function withHashFragment(path, fragment) {
  const slug = String(path || '').split('#')[0].trim();
  if (!slug) return '';
  const normalized = slug.startsWith('/') ? slug : `/${slug}`;
  const hash = String(fragment || '').replace(/^#/, '').trim();
  return hash ? `${normalized}#${hash}` : normalized;
}

function keptCustomParagraph(prevValue, sourceDescription) {
  const prev = String(prevValue || '').trim();
  const source = String(sourceDescription || '').trim();
  if (!prev) return '';
  if (source && prev === source) return '';
  return prev;
}

function locationCode(value) {
  if (value && typeof value === 'object') return String(value.code || '');
  return String(value || '');
}

/**
 * @param {{ data?: object, onChange: (patch: object) => void, lang?: string, country?: string }} props
 */
export default function CarouselSectionEditor({ data = {}, onChange, lang = '', country = '' }) {
  const slides = Array.isArray(data.slides) ? data.slides : [];
  const langKey = String(lang?.code || lang || '').toUpperCase();

  const [expandedSlideIds, setExpandedSlideIds] = useState(() => new Set());
  const [expandedCityIds, setExpandedCityIds] = useState(() => new Set());
  const [entryPages, setEntryPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingPortsFor, setLoadingPortsFor] = useState('');
  const autoLoadedCityKeys = useRef(new Set());

  const allCityIds = slides.flatMap((s) =>
    (Array.isArray(s.cities) ? s.cities : []).map((c) => c.id).filter(Boolean)
  );

  const loadEntryPages = useCallback(async () => {
    setLoadingPages(true);
    try {
      let pages = [];
      try {
        const summary = await api.get('/entry_pages/summary');
        pages = Array.isArray(summary.data) ? summary.data : [];
      } catch {
        const fallback = await api.get('/entry_pages/translations');
        pages = Array.isArray(fallback.data) ? fallback.data : [];
      }
      setEntryPages(pages);
    } catch (err) {
      console.error('CarouselSectionEditor: failed to load entry pages', err);
      setEntryPages([]);
    } finally {
      setLoadingPages(false);
    }
  }, []);

  useEffect(() => {
    loadEntryPages();
  }, [loadEntryPages]);

  useEffect(() => {
    const currentSlideIds = new Set(slides.map((s) => s.id).filter(Boolean));
    setExpandedSlideIds((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        if (currentSlideIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [slides]);

  useEffect(() => {
    const currentCityIds = new Set(allCityIds);
    setExpandedCityIds((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        if (currentCityIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [allCityIds.join('|')]);

  const pageOptions = useMemo(() => {
    return (entryPages || [])
      .filter((p) => pageMatchesLang(p, langKey))
      .map(toPageOption)
      .filter((opt) => opt.value);
  }, [entryPages, langKey]);

  const countryCode = String(country || '').toLowerCase();
  const stateOptions = useMemo(() => {
    const border = countryCode === 'canada' ? 'ca' : countryCode === 'mexico' ? 'mexico' : '';
    return (BORDER_STATES || [])
      .filter((s) => !s.disabled && (!border || s.border === border))
      .map((s) => ({ label: s.name, value: s.code }));
  }, [countryCode]);

  const setSlides = (nextSlides) => onChange({ slides: nextSlides });

  const isSlideExpanded = (slideId) => Boolean(slideId && expandedSlideIds.has(slideId));
  const isCityExpanded = (cityId) => Boolean(cityId && expandedCityIds.has(cityId));

  const toggleInSet = (setter) => (id) => {
    if (!id) return;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandInSet = (setter) => (id) => {
    if (!id) return;
    setter((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const toggleSlide = toggleInSet(setExpandedSlideIds);
  const expandSlide = expandInSet(setExpandedSlideIds);
  const toggleCity = toggleInSet(setExpandedCityIds);
  const expandCity = expandInSet(setExpandedCityIds);

  const updateSlide = (slideIndex, patch) => {
    setSlides(
      slides.map((slide, i) => (i === slideIndex ? { ...slide, ...patch } : slide))
    );
  };

  const updateCity = (slideIndex, cityIndex, patch) => {
    const slide = slides[slideIndex] || {};
    const cities = Array.isArray(slide.cities) ? slide.cities : [];
    updateSlide(slideIndex, {
      cities: cities.map((city, i) => (i === cityIndex ? { ...city, ...patch } : city)),
    });
  };

  const portKey = (entry) =>
    `${entry.entry_page_id || ''}|${entry.port_number || ''}|${entry.crossing_key || ''}`;

  const ensureFullPage = async (pageId) => {
    if (!pageId) return null;
    let page = entryPages.find((p) => String(p._id) === String(pageId));
    const crossings = page?.crossing_names_section?.crossings_names;
    if (
      page &&
      Array.isArray(crossings) &&
      crossings.length &&
      page?.image?.url
    ) {
      return page;
    }
    try {
      const res = await api.get(`/entry_pages/${pageId}`);
      page = res.data;
      setEntryPages((prev) => {
        const id = String(pageId);
        const without = (prev || []).filter((p) => String(p._id) !== id);
        return [...without, page];
      });
      return page;
    } catch (err) {
      console.error('CarouselSectionEditor: failed to load entry page crossings', err);
      return page || null;
    }
  };

  const entriesFromPage = (page, pageId) =>
    portsFromEntryPage(page).map((row) => ({
      ...defaultCarouselEntry(),
      ...row,
      entry_page_id: String(pageId),
    }));

  const matchingPagesForCity = (slide, cityCode) => {
    const stateCode = slide?.state_code || '';
    const cityName =
      (BORDER_CITIES_BY_STATE[stateCode] || []).find((c) => c.code === cityCode)?.name || '';
    const cityNeedle = normalizeMatchText(cityName || cityCode);

    const byDeparture = pageOptions.filter((opt) => {
      const page = opt.page || {};
      const dep = page.departure || {};
      const pageState = locationCode(dep.state || page.state);
      const pageCity = locationCode(dep.city || page.city);
      if (stateCode && pageState && pageState.toUpperCase() !== String(stateCode).toUpperCase()) {
        return false;
      }
      if (!cityCode) return false;
      if (pageCity === String(cityCode)) return true;
      if (cityName && pageCity === cityName) return true;
      if (cityNeedle && normalizeMatchText(pageCity) === cityNeedle) return true;
      return false;
    });
    if (byDeparture.length) return byDeparture;

    if (!cityNeedle) return [];
    return pageOptions.filter((opt) => {
      const slug = normalizeMatchText(opt.page?.full_slug || opt.page?.slug || '');
      const name = normalizeMatchText(opt.page?.crossing_names_section?.name || opt.label || '');
      return slug.includes(cityNeedle) || name.includes(cityNeedle);
    });
  };

  const mergeCrossingEntries = (existing, incoming) => {
    const existingByKey = new Map((existing || []).map((row) => [portKey(row), row]));
    const seen = new Set();
    const next = [];
    for (const row of incoming || []) {
      const key = portKey(row);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const prev = existingByKey.get(key);
      if (prev) {
        const { source_description, ...incomingRow } = row;
        next.push({
          ...prev,
          ...incomingRow,
          id: prev.id,
          custom_description: keptCustomParagraph(prev.custom_description, source_description),
          mobile_name: incomingRow.mobile_name || prev.mobile_name || '',
          custom_title: incomingRow.mobile_name || incomingRow.custom_title || prev.custom_title || '',
          link_url: incomingRow.link_url || prev.link_url || '',
          image: hasCarouselEntryImage(prev.image)
            ? prev.image
            : normalizeCarouselEntryImage(incomingRow.image),
        });
      } else {
        const { source_description: _source, ...incomingRow } = row;
        next.push({ ...incomingRow, custom_description: '' });
      }
    }
    for (const row of existing || []) {
      const key = portKey(row);
      if (!seen.has(key)) next.push(row);
    }
    return next;
  };

  const loadPortsFromPage = async (slideIndex, cityIndex, pageId) => {
    if (!pageId) return;
    const city = slides[slideIndex]?.cities?.[cityIndex];
    const loadKey = city?.id || `${slideIndex}-${cityIndex}`;
    setLoadingPortsFor(loadKey);
    try {
      const page = await ensureFullPage(pageId);
      if (!page) return;
      const currentCity = slides[slideIndex]?.cities?.[cityIndex] || {};
      const existing = Array.isArray(currentCity.entries) ? currentCity.entries : [];
      updateCity(slideIndex, cityIndex, {
        entries: mergeCrossingEntries(existing, entriesFromPage(page, pageId)),
      });
      if (currentCity.id) expandCity(currentCity.id);
    } finally {
      setLoadingPortsFor('');
    }
  };

  const liveMobileTitle = (entry) => {
    const page = entryPages.find((p) => String(p._id) === String(entry?.entry_page_id));
    const key = String(entry?.crossing_key || '').replace(/^#/, '').trim();
    const crossings = page?.crossing_names_section?.crossings_names || [];
    const match = crossings.find((crossing) => {
      const fragment = String(crossing?.urlFragment || crossing?.key || '')
        .replace(/^#/, '')
        .trim();
      return Boolean(key && fragment === key);
    });
    return String(
      match?.mobileName ||
        match?.mobile_name ||
        entry?.mobile_name ||
        entry?.custom_title ||
        ''
    ).trim();
  };

  const liveEntryUrl = (entry) => {
    const page = entryPages.find((p) => String(p._id) === String(entry?.entry_page_id));
    const slug =
      page?.full_slug ||
      page?.slug ||
      String(entry?.link_url || '').split('#')[0];
    const key = String(entry?.crossing_key || '').replace(/^#/, '').trim();
    const crossings = page?.crossing_names_section?.crossings_names || [];
    const match = crossings.find((crossing) => {
      const fragment = String(crossing?.urlFragment || crossing?.key || '')
        .replace(/^#/, '')
        .trim();
      return Boolean(key && fragment === key);
    });
    const fragment = String(
      match?.urlFragment || match?.key || key || ''
    ).replace(/^#/, '').trim();
    return withHashFragment(slug, fragment) || String(entry?.link_url || '').trim();
  };

  const liveCardImage = (entry) => {
    if (hasCarouselEntryImage(entry?.image)) return entry.image;
    const page = entryPages.find((p) => String(p._id) === String(entry?.entry_page_id));
    return page?.image || null;
  };

  const updateCityEntry = (slideIndex, cityIndex, entryIndex, patch) => {
    const slide = slides[slideIndex] || {};
    const cities = Array.isArray(slide.cities) ? slide.cities : [];
    const city = cities[cityIndex] || {};
    const entries = Array.isArray(city.entries) ? city.entries : [];
    updateCity(slideIndex, cityIndex, {
      entries: entries.map((entry, i) => (i === entryIndex ? { ...entry, ...patch } : entry)),
    });
  };

  const removeCityEntry = (slideIndex, cityIndex, entryIndex) => {
    const slide = slides[slideIndex] || {};
    const cities = Array.isArray(slide.cities) ? slide.cities : [];
    const city = cities[cityIndex] || {};
    const entries = Array.isArray(city.entries) ? city.entries : [];
    updateCity(slideIndex, cityIndex, {
      entries: entries.filter((_, i) => i !== entryIndex),
    });
  };

  const applyStateCode = (slideIndex, stateCode) => {
    const state = (BORDER_STATES || []).find((s) => s.code === stateCode);
    const slide = slides[slideIndex] || {};
    const cities = (Array.isArray(slide.cities) ? slide.cities : []).map((city) => ({
      ...city,
      city_code: '',
      entries: [],
    }));
    updateSlide(slideIndex, {
      state_code: stateCode || '',
      badge: state?.name || slide.badge || '',
      cities,
    });
  };

  const applyCityCode = async (slideIndex, cityIndex, cityCode) => {
    const slide = slides[slideIndex] || {};
    const city = (Array.isArray(slide.cities) ? slide.cities : [])[cityIndex] || {};
    const citiesForState = BORDER_CITIES_BY_STATE[slide.state_code] || [];
    const match = citiesForState.find((c) => c.code === cityCode);
    const patch = {
      city_code: cityCode || '',
      badge: match?.name || '',
    };

    if (slide.id) expandSlide(slide.id);
    if (city.id) {
      expandCity(city.id);
      autoLoadedCityKeys.current.add(city.id);
    }

    updateCity(slideIndex, cityIndex, !cityCode ? { ...patch, entries: [] } : patch);

    if (!cityCode) {
      return;
    }

    const loadKey = city.id || `${slideIndex}-${cityIndex}`;
    setLoadingPortsFor(loadKey);
    try {
      const hasCrossingCards = (Array.isArray(city.entries) ? city.entries : []).some(
        (entry) => String(entry.crossing_key || '').trim() || String(entry.port_number || '').trim()
      );
      const keepExisting = city.city_code === cityCode && hasCrossingCards;
      const existing = keepExisting ? city.entries : [];
      const matches = matchingPagesForCity(slide, cityCode);
      let incoming = [];
      for (const opt of matches) {
        const page = await ensureFullPage(opt.value);
        if (!page) continue;
        incoming = incoming.concat(entriesFromPage(page, opt.value));
      }
      updateCity(slideIndex, cityIndex, {
        ...patch,
        entries: mergeCrossingEntries(existing, incoming),
      });
      if (city.id) {
        expandCity(city.id);
        scrollToId(`carousel-city-${city.id}`);
      }
    } finally {
      setLoadingPortsFor('');
    }
  };

  useEffect(() => {
    if (loadingPages || !pageOptions.length) return;
    slides.forEach((slide, slideIndex) => {
      (Array.isArray(slide.cities) ? slide.cities : []).forEach((city, cityIndex) => {
        if (!city?.id || !city?.city_code) return;
        if (autoLoadedCityKeys.current.has(city.id)) return;
        autoLoadedCityKeys.current.add(city.id);
        applyCityCode(slideIndex, cityIndex, city.city_code);
      });
    });
  }, [loadingPages, pageOptions.length, slides]);

  const cityPageOptions = (slide, city) => {
    const stateCode = slide?.state_code || '';
    const cityCode = city?.city_code || '';
    const filtered = pageOptions.filter((opt) => {
      if (!stateCode && !cityCode) return true;
      const dep = opt.page?.departure || {};
      if (stateCode && String(dep.state || '').toUpperCase() !== String(stateCode).toUpperCase()) {
        return false;
      }
      if (cityCode && String(dep.city || '') !== String(cityCode)) return false;
      return true;
    });
    return filtered.length ? filtered : pageOptions;
  };

  const addState = () => {
    const created = defaultCarouselSlide();
    setSlides([...slides, created]);
    expandSlide(created.id);
    const firstCity = created.cities?.[0];
    if (firstCity?.id) expandCity(firstCity.id);
    scrollToId(`carousel-slide-${created.id}`);
  };

  const addCity = (slideIndex) => {
    const slide = slides[slideIndex] || {};
    const cities = Array.isArray(slide.cities) ? slide.cities : [];
    const created = defaultCarouselCity();
    expandSlide(slide.id);
    expandCity(created.id);
    updateSlide(slideIndex, { cities: [...cities, created] });
    scrollToId(`carousel-city-${created.id}`);
  };

  return (
    <div className="grid">
      <div className="col-12">
        <MessageLike
          langKey={langKey}
          pageCount={pageOptions.length}
          loading={loadingPages}
          onReload={loadEntryPages}
        />
      </div>

      <div className="col-12">
        <div className="flex justify-content-between align-items-center mb-2">
          <strong>State slides ({slides.length})</strong>
          <Button
            type="button"
            label="Add state"
            icon="pi pi-plus"
            className="p-button-sm"
            onClick={addState}
          />
        </div>

        <div className="flex flex-column gap-3">
          {slides.map((slide, slideIndex) => {
            const cities = Array.isArray(slide.cities) ? slide.cities : [];
            const expanded = isSlideExpanded(slide.id);
            const slideDomId = `carousel-slide-${slide.id || slideIndex}`;

            return (
              <div
                key={slide.id || `slide-${slideIndex}`}
                id={slideDomId}
                className="border-1 border-round surface-border"
                style={{ background: '#f8fafc', scrollMarginTop: 16 }}
              >
                <div className="flex justify-content-between align-items-center p-3">
                  <button
                    type="button"
                    className="flex align-items-center gap-2 p-0 border-none bg-transparent cursor-pointer"
                    onClick={() => toggleSlide(slide.id)}
                    aria-expanded={expanded}
                  >
                    <i
                      className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                      style={{ fontSize: 12 }}
                    />
                    <span className="font-medium">
                      State {slideIndex + 1}
                      {slide.badge ? `: ${slide.badge}` : ''}
                    </span>
                  </button>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      icon="pi pi-arrow-up"
                      className="p-button-text p-button-sm"
                      disabled={slideIndex === 0}
                      onClick={() => setSlides(moveItem(slides, slideIndex, -1))}
                    />
                    <Button
                      type="button"
                      icon="pi pi-arrow-down"
                      className="p-button-text p-button-sm"
                      disabled={slideIndex === slides.length - 1}
                      onClick={() => setSlides(moveItem(slides, slideIndex, 1))}
                    />
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="p-button-danger p-button-text p-button-sm"
                      disabled={slides.length <= 1}
                      onClick={() =>
                        setSlides(slides.filter((_, i) => i !== slideIndex))
                      }
                    />
                  </div>
                </div>

                {expanded ? (
                  <div className="px-3 pb-3">
                    <div className="grid">
                      <div className="col-12 md:col-6">
                        <label>
                          <strong>State</strong>
                        </label>
                        <Dropdown
                          value={slide.state_code || null}
                          options={stateOptions}
                          optionLabel="label"
                          optionValue="value"
                          placeholder={countryCode ? 'Select state' : 'Set country first'}
                          className="w-full"
                          filter
                          showClear
                          disabled={!stateOptions.length}
                          onChange={(e) => applyStateCode(slideIndex, e.value)}
                        />
                      </div>
                      <div className="col-12 md:col-6">
                        <label>
                          <strong>State badge</strong>
                        </label>
                        <InputText
                          value={slide.badge || ''}
                          onChange={(e) =>
                            updateSlide(slideIndex, { badge: e.target.value })
                          }
                          className="w-full"
                          placeholder="Baja California"
                        />
                      </div>
                      <div className="col-12 md:col-6">
                        <label>
                          <strong>Eyebrow</strong>
                        </label>
                        <InputText
                          value={slide.eyebrow || ''}
                          onChange={(e) =>
                            updateSlide(slideIndex, { eyebrow: e.target.value })
                          }
                          className="w-full"
                          placeholder="Placeholder text"
                        />
                      </div>
                      <div className="col-12 md:col-3">
                        <label>
                          <strong>Title level</strong>
                        </label>
                        <Dropdown
                          value={clampHeadingLevel(slide.title_level ?? 2)}
                          options={HEADING_LEVEL_OPTIONS}
                          onChange={(e) =>
                            updateSlide(slideIndex, { title_level: e.value })
                          }
                          className="w-full"
                        />
                      </div>
                      <div className="col-12 md:col-9">
                        <label>
                          <strong>Title</strong>
                        </label>
                        <InputText
                          value={slide.title || ''}
                          onChange={(e) =>
                            updateSlide(slideIndex, { title: e.target.value })
                          }
                          className="w-full"
                          placeholder="Placeholder text"
                        />
                      </div>
                      <div className="col-12">
                        <label>
                          <strong>Paragraph</strong>
                        </label>
                        <InputTextarea
                          value={slide.description || ''}
                          onChange={(e) =>
                            updateSlide(slideIndex, { description: e.target.value })
                          }
                          rows={3}
                          className="w-full"
                          placeholder="Placeholder text"
                        />
                      </div>
                    </div>

                    <div className="mt-3 pt-3" style={{ borderTop: '1px dashed #cbd5e1' }}>
                      <div className="flex justify-content-between align-items-center mb-2">
                        <strong style={{ color: '#16a34a' }}>
                          Cities / internal carousel ({cities.length})
                        </strong>
                        <Button
                          type="button"
                          label="Add city"
                          icon="pi pi-plus"
                          className="p-button-sm p-button-outlined"
                          onClick={() => addCity(slideIndex)}
                        />
                      </div>

                      <div className="flex flex-column gap-2">
                        {cities.map((city, cityIndex) => {
                          const cityExpanded = isCityExpanded(city.id);
                          return (
                            <div
                              key={city.id || `city-${slideIndex}-${cityIndex}`}
                              id={`carousel-city-${city.id || `${slideIndex}-${cityIndex}`}`}
                              className="border-1 border-round surface-border bg-white"
                              style={{ scrollMarginTop: 16 }}
                            >
                              <div className="flex justify-content-between align-items-center p-2">
                                <button
                                  type="button"
                                  className="flex align-items-center gap-2 p-0 border-none bg-transparent cursor-pointer"
                                  onClick={() => toggleCity(city.id)}
                                  aria-expanded={cityExpanded}
                                  style={{ color: '#16a34a' }}
                                >
                                  <i
                                    className={`pi ${cityExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                                    style={{ fontSize: 11 }}
                                  />
                                  <span className="text-sm font-medium">
                                    City {cityIndex + 1}
                                    {city.badge ? `: ${city.badge}` : ''}
                                  </span>
                                </button>
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    icon="pi pi-arrow-up"
                                    className="p-button-text p-button-sm"
                                    disabled={cityIndex === 0}
                                    onClick={() =>
                                      updateSlide(slideIndex, {
                                        cities: moveItem(cities, cityIndex, -1),
                                      })
                                    }
                                  />
                                  <Button
                                    type="button"
                                    icon="pi pi-arrow-down"
                                    className="p-button-text p-button-sm"
                                    disabled={cityIndex === cities.length - 1}
                                    onClick={() =>
                                      updateSlide(slideIndex, {
                                        cities: moveItem(cities, cityIndex, 1),
                                      })
                                    }
                                  />
                                  <Button
                                    type="button"
                                    icon="pi pi-trash"
                                    className="p-button-danger p-button-text p-button-sm"
                                    disabled={cities.length <= 1}
                                    onClick={() =>
                                      updateSlide(slideIndex, {
                                        cities: cities.filter((_, i) => i !== cityIndex),
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              {cityExpanded ? (
                                <div className="px-2 pb-2">
                                  <div className="grid">
                                    <div className="col-12 md:col-6">
                                      <label>
                                        <strong>City</strong>
                                      </label>
                                      <Dropdown
                                        value={city.city_code || null}
                                        options={(BORDER_CITIES_BY_STATE[slide.state_code] || [])
                                          .filter((c) => !c.disabled)
                                          .map((c) => ({ label: c.name, value: c.code }))}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder={slide.state_code ? 'Select city' : 'Select state first'}
                                        className="w-full"
                                        filter
                                        showClear
                                        disabled={!slide.state_code || loadingPortsFor === (city.id || '')}
                                        onChange={(e) =>
                                          applyCityCode(slideIndex, cityIndex, e.value)
                                        }
                                      />
                                    </div>
                                    <div className="col-12 md:col-6">
                                      <label>
                                        <strong>City badge</strong>
                                      </label>
                                      <InputText
                                        value={city.badge || ''}
                                        onChange={(e) =>
                                          updateCity(slideIndex, cityIndex, {
                                            badge: e.target.value,
                                          })
                                        }
                                        className="w-full"
                                        placeholder="Tijuana"
                                      />
                                    </div>
                                    <div className="col-12 md:col-6">
                                      <label>
                                        <strong>Green eyebrow</strong>
                                      </label>
                                      <InputText
                                        value={city.eyebrow || ''}
                                        onChange={(e) =>
                                          updateCity(slideIndex, cityIndex, {
                                            eyebrow: e.target.value,
                                          })
                                        }
                                        className="w-full"
                                        placeholder="Placeholder text"
                                      />
                                    </div>
                                    <div className="col-12 md:col-3">
                                      <label>
                                        <strong>Title level</strong>
                                      </label>
                                      <Dropdown
                                        value={clampHeadingLevel(city.title_level ?? 3)}
                                        options={HEADING_LEVEL_OPTIONS}
                                        onChange={(e) =>
                                          updateCity(slideIndex, cityIndex, {
                                            title_level: e.value,
                                          })
                                        }
                                        className="w-full"
                                      />
                                    </div>
                                    <div className="col-12 md:col-9">
                                      <label>
                                        <strong>Title</strong>
                                      </label>
                                      <InputText
                                        value={city.title || ''}
                                        onChange={(e) =>
                                          updateCity(slideIndex, cityIndex, {
                                            title: e.target.value,
                                          })
                                        }
                                        className="w-full"
                                        placeholder="Placeholder text"
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label>
                                        <strong>Paragraph</strong>
                                      </label>
                                      <InputTextarea
                                        value={city.description || ''}
                                        onChange={(e) =>
                                          updateCity(slideIndex, cityIndex, {
                                            description: e.target.value,
                                          })
                                        }
                                        rows={3}
                                        autoResize
                                        className="w-full"
                                        placeholder="Placeholder text"
                                      />
                                    </div>

                                    <div className="col-12">
                                      <div
                                        className="mt-2 pt-2"
                                        style={{ borderTop: '1px dashed #cbd5e1' }}
                                      >
                                        <strong className="block mb-2">
                                          Crossing names ({(city.entries || []).length})
                                        </strong>
                                        <small className="text-600 block mb-2">
                                          Selecting a city loads every crossing name as its own card. The title comes from Name in Mobile apps and is read-only. Custom paragraph is editable. Use the dropdown only to add crossings from another entry page.
                                        </small>
                                        <Dropdown
                                          value={null}
                                          options={cityPageOptions(slide, city)}
                                          optionLabel="label"
                                          optionValue="value"
                                          placeholder={
                                            !langKey
                                              ? 'Set country page language first'
                                              : loadingPortsFor === (city.id || '')
                                                ? 'Loading crossing names…'
                                                : 'Add crossings from another entry page'
                                          }
                                          className="w-full"
                                          filter
                                          filterBy="label"
                                          disabled={!langKey || loadingPages || loadingPortsFor === (city.id || '')}
                                          onChange={(e) =>
                                            e.value && loadPortsFromPage(slideIndex, cityIndex, e.value)
                                          }
                                        />
                                      </div>
                                    </div>
                                    {(city.entries || []).map((entry, entryIndex) => (
                                        <div
                                          key={entry.id || `entry-${slideIndex}-${cityIndex}-${entryIndex}`}
                                          className="col-12"
                                        >
                                          <div className="border-1 border-round p-2 surface-ground">
                                            <div className="flex justify-content-between align-items-center mb-2">
                                              <span className="text-sm font-medium">
                                                {liveMobileTitle(entry) || entry.crossing_name || entry.port_name || `Crossing ${entryIndex + 1}`}
                                                {entry.port_number ? ` (${entry.port_number})` : ''}
                                              </span>
                                              <Button
                                                type="button"
                                                icon="pi pi-trash"
                                                className="p-button-text p-button-danger p-button-sm"
                                                onClick={() =>
                                                  removeCityEntry(slideIndex, cityIndex, entryIndex)
                                                }
                                              />
                                            </div>
                                            {liveEntryUrl(entry) ? (
                                              <>
                                                <label>
                                                  <strong>URL</strong>
                                                </label>
                                                <small className="text-600 block mb-2">
                                                  {liveEntryUrl(entry)}
                                                </small>
                                              </>
                                            ) : null}
                                            <label>
                                              <strong>Title from mobile</strong>
                                            </label>
                                            <InputText
                                              value={liveMobileTitle(entry)}
                                              disabled
                                              className="w-full mb-1"
                                              placeholder="No mobile title on this crossing"
                                            />
                                            <small className="text-600 block mb-2">
                                              From <strong>Name in Mobile apps</strong> on this crossing. Edit it on the entry page; it cannot be changed here.
                                            </small>
                                            <label>
                                              <strong>Custom paragraph</strong>
                                            </label>
                                            <InputTextarea
                                              value={entry.custom_description || ''}
                                              onChange={(e) =>
                                                updateCityEntry(slideIndex, cityIndex, entryIndex, {
                                                  custom_description: e.target.value,
                                                })
                                              }
                                              rows={3}
                                              className="w-full mb-2"
                                              placeholder="Write card copy for this crossing"
                                            />
                                            <label>
                                              <strong>Card image</strong>
                                            </label>
                                            <small className="text-600 block mb-2">
                                              Optional. If empty, the card uses the entry page image.
                                            </small>
                                            {liveCardImage(entry)?.url ? (
                                              <img
                                                src={liveCardImage(entry).url}
                                                alt={liveCardImage(entry).alt || liveMobileTitle(entry) || ''}
                                                style={{
                                                  width: '100%',
                                                  maxHeight: 140,
                                                  objectFit: 'cover',
                                                  borderRadius: 8,
                                                  marginBottom: 8,
                                                }}
                                              />
                                            ) : null}
                                            <small className="text-600 block mb-2">
                                              {hasCarouselEntryImage(entry.image)
                                                ? 'Custom image for this crossing'
                                                : liveCardImage(entry)?.url
                                                  ? 'Showing entry page image (fallback)'
                                                  : 'No image yet'}
                                            </small>
                                            <div className="flex gap-2">
                                              <Button
                                                type="button"
                                                label="Upload"
                                                icon="pi pi-cloud-upload"
                                                className="p-button-sm"
                                                onClick={() =>
                                                  openCloudinary(({ url, width, height }) =>
                                                    updateCityEntry(slideIndex, cityIndex, entryIndex, {
                                                      image: {
                                                        ...normalizeCarouselEntryImage(entry.image),
                                                        url,
                                                        ...(width ? { width } : {}),
                                                        ...(height ? { height } : {}),
                                                      },
                                                    })
                                                  )
                                                }
                                              />
                                              {hasCarouselEntryImage(entry.image) ? (
                                                <Button
                                                  type="button"
                                                  label="Remove"
                                                  icon="pi pi-times"
                                                  className="p-button-sm p-button-outlined p-button-danger"
                                                  onClick={() =>
                                                    updateCityEntry(slideIndex, cityIndex, entryIndex, {
                                                      image: normalizeCarouselEntryImage(null),
                                                    })
                                                  }
                                                />
                                              ) : null}
                                            </div>
                                          </div>
                                        </div>
                                    ))}
                                    {(city.entries || []).length === 0 ? (
                                      <div className="col-12">
                                        <p className="text-sm text-500 m-0">
                                          {loadingPortsFor === (city.id || '')
                                            ? 'Loading crossing names…'
                                            : 'Select a city to load its crossing names. Each card title comes from the mobile name and stays disabled.'}
                                        </p>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MessageLike({ langKey, pageCount, loading, onReload }) {
  return (
    <div
      className="flex flex-wrap align-items-center justify-content-between gap-2 p-2 border-round mb-2"
      style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
    >
      <small className="text-700">
        {loading
          ? 'Loading entry pages…'
          : langKey
            ? `${pageCount} entry page${pageCount === 1 ? '' : 's'} for ${langKey}. Per city: select state → city; crossing names load automatically. Card title is the mobile name (read-only).`
            : 'Set the country page language to filter entry pages for each city.'}
      </small>
      <Button
        type="button"
        label="Reload pages"
        icon="pi pi-refresh"
        className="p-button-text p-button-sm"
        onClick={onReload}
        loading={loading}
      />
    </div>
  );
}
